import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { posts } from "@/data/posts";

const categories = ["Alle", "Gesundheit", "Finanzen", "Lifestyle"];

export default function BlogListPage() {
  const [category, setCategory] = useState("Alle");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      const matchesCat = category === "Alle" || p.category === category;
      const matchesQ =
        !query ||
        p.title.toLowerCase().includes(query.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(query.toLowerCase());
      return matchesCat && matchesQ;
    });
  }, [category, query]);

  const [featured, ...rest] = filtered;

  return (
    <>
      <PageHero
        chapter="IV"
        eyebrow="Beiträge"
        title="Wissen &"
        italic="Inspiration."
        intro="Gedanken, Rezepte und kleine Werkzeuge rund um Gesundheit, Finanzen und den Alltag dazwischen."
        tone="sage"
      />

      {/* Filter + search */}
      <section className="relative py-10 bg-bone-100 border-b border-bone-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`blog-filter-${c.toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`px-4 h-10 rounded-full text-[13px] tracking-wide transition-all duration-300 ${
                  category === c
                    ? "bg-ink-900 text-bone-50"
                    : "bg-bone-200 text-ink-700 hover:bg-bone-300/70"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative max-w-sm w-full">
            <Search strokeWidth={1.75} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Beiträge durchsuchen …"
              data-testid="blog-search-input"
              className="w-full h-11 pl-11 pr-4 rounded-full bg-bone-200 border border-bone-300 text-[14px] placeholder:text-ink-500 focus:outline-none focus:border-sage-400 focus:bg-bone-50 transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="relative py-16 bg-bone-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Link
              to={`/beitraege/${featured.slug}`}
              data-testid="blog-featured-post"
              className="group grid lg:grid-cols-12 gap-8 items-center bg-bone-50 rounded-[2rem] overflow-hidden border border-bone-300 hover:shadow-lift transition-all duration-500"
            >
              <div className="lg:col-span-7 relative h-72 lg:h-[460px] overflow-hidden">
                <img
                  src={featured.img}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                />
                <span className={`absolute top-5 left-5 px-3 h-7 inline-flex items-center rounded-full font-mono-fine text-[10px] tracking-[0.2em] uppercase ${featured.chip}`}>
                  {featured.category}
                </span>
              </div>
              <div className="lg:col-span-5 p-8 lg:p-12">
                <div className="flex items-center gap-3 font-mono-fine text-[11px] tracking-[0.2em] uppercase text-ink-500">
                  <span>Empfohlen</span>
                  <span className="w-1 h-1 rounded-full bg-bone-300" />
                  <span>{featured.date}</span>
                </div>
                <h2 className="mt-4 font-display font-light text-3xl md:text-4xl lg:text-[3rem] leading-[1.02] tracking-[-0.03em] text-ink-900 group-hover:text-sage-700 transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-ink-700 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase font-mono-fine text-ink-900">
                  Weiterlesen
                  <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* List */}
      <section className="relative py-16 md:py-24 bg-bone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-ink-500 font-display italic text-xl" data-testid="blog-empty-state">
              Keine Beiträge gefunden.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map((p) => (
                <Link
                  key={p.slug}
                  to={`/beitraege/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group bg-bone-50 rounded-[1.5rem] overflow-hidden border border-bone-300 hover:shadow-lift hover:-translate-y-1 transition-all duration-500 flex flex-col"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.title}
                      className="w-full h-56 object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    />
                    <span className={`absolute top-4 left-4 px-3 h-7 inline-flex items-center rounded-full font-mono-fine text-[10px] tracking-[0.2em] uppercase ${p.chip}`}>
                      {p.category}
                    </span>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 font-mono-fine text-[10px] tracking-[0.18em] uppercase text-ink-500">
                      <span>{p.date}</span>
                      <span className="w-1 h-1 rounded-full bg-bone-300" />
                      <span>{p.read}</span>
                    </div>
                    <h3 className="mt-3 font-display font-light text-[1.55rem] leading-[1.1] tracking-[-0.02em] text-ink-900 group-hover:text-sage-700 transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-ink-700 text-[15px] leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] tracking-[0.12em] uppercase font-mono-fine text-ink-900">
                      Weiterlesen
                      <ArrowUpRight strokeWidth={1.5} className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
